import { Modal, Radio, ScrollArea, useToast } from '@ethsign/ui';

import { useQuery } from '@tanstack/react-query';

import React, { useEffect, useMemo, useRef } from 'react';

import {
  ImageUploadWithCropper,
  SignatureCanvas,
  SignatureStyleTypeEnum,
  SignInputTab,
  SignPreview,
  Tabs
} from '@/components';
import { SignatureCanvasRef } from '@/components/SignatureCanvas';
import { useUserInfo } from '@/hooks';
import { fetchSignatureStyles } from '@/services';
import { textToBase64 } from '@/utils';
import { useViewModel } from '@/utils/bizify';

import { PageCtrl } from './pageCtrl';

const SignStylesList = ({ data, value, onChange }: { data: any[]; value: string; onChange: (v: string) => void }) => {
  const { user } = useUserInfo();

  const options = useMemo(() => {
    return data?.map((item: any, i: number) => {
      return {
        label: <SignPreview src={item.image} address={user?.displayAddress} key={i} />,
        value: item.image
      };
    });
  }, [data]);

  if (!options?.length) {
    return (
      <div className="h-[262px] flex w-full items-center justify-center text-gray-400 opacity-60 text-xs">
        There&apos;s nothing here 😴
      </div>
    );
  }
  return (
    <ScrollArea className={'h-[262px]'}>
      <Radio options={options || []} value={value} onValueChange={onChange} itemClassName={'items-center'} />
    </ScrollArea>
  );
};

export const SignatureStyleModal = (props: {
  onConfirm: (img: string) => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose?: () => void;
}) => {
  const { onConfirm, open, onOpenChange } = props;
  const { user } = useUserInfo();
  const { toast } = useToast();
  const defaultName = user?.nickName || '';
  const vm = useViewModel(PageCtrl, { textSign: defaultName });
  const vmData = vm.$useSnapshot();
  const ref = useRef<SignatureCanvasRef>(null);
  const copperRef = useRef<{ getImage: () => string }>(null);
  const { data } = useQuery({
    queryKey: ['signature-styles'],
    queryFn: () => fetchSignatureStyles(),
    select: (res) => res.rows
  });

  useEffect(() => {
    if (data?.length && vmData.activityKey !== SignatureStyleTypeEnum.STYLES) {
      vm.setSignStyle(data[0].image);
      vm.setActivityKey(SignatureStyleTypeEnum.STYLES);
    }
  }, [data]);

  const onClose = (isSave = false) => {
    onOpenChange(false);
    // not clean textSign when save
    if (!isSave) {
      vm.setTextSign(defaultName);
    }
  };

  const handleConfirm = async () => {
    vm.setLoading(true);
    try {
      let image = '';
      let errorMessage = '';
      if (vmData.activityKey === SignatureStyleTypeEnum.DRAW) {
        if (ref.current?.isEmpty()) {
          errorMessage = 'Please sign your name';
        }
        image = ref.current?.getTrimmedCanvas().toDataURL() as string;
      } else if (vmData.activityKey === SignatureStyleTypeEnum.TYPE) {
        if (!vmData.textSign) {
          errorMessage = 'Please enter your name';
        }
        image = textToBase64(vmData.textSign);
      } else if (vmData.activityKey === SignatureStyleTypeEnum.STYLES) {
        if (!vmData.signStyle) {
          errorMessage = 'Please select signature style';
        }
        image = vmData.signStyle;
      } else {
        const uploadSign = copperRef.current?.getImage() as string;
        if (!uploadSign) {
          errorMessage = 'Please upload your signature';
        }
        image = uploadSign;
      }
      if (errorMessage) {
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'error'
        });
        return;
      }
      onConfirm(image);
      onClose(true);
    } finally {
      vm.setLoading(false);
    }
  };
  return (
    <>
      <Modal
        onClose={() => props.onClose && props.onClose()}
        open={open}
        onOpenChange={onOpenChange}
        header={
          <>
            <div className="text-lg font-medium text-gray-900">Add Signature Style</div>
            <div className="text-sm text-gray-500 mt-[8px]">Update your signature style</div>
          </>
        }
        onCancel={() => {
          props.onClose && props.onClose();
          onClose();
        }}
        onConfirm={handleConfirm}
        confirmButtonProps={{
          loading: vmData.loading,
          variant: 'primary'
        }}
        confirmText={'Save'}
      >
        <div className="mt-[20px]">
          <Tabs
            onChange={(k) => vm.setActivityKey(k as SignatureStyleTypeEnum)}
            value={vmData.activityKey}
            items={[
              {
                key: SignatureStyleTypeEnum.TYPE,
                label: 'Type',
                children: (
                  <SignInputTab value={vmData.textSign} onChange={vm.setTextSign} address={user?.displayAddress} />
                )
              },
              {
                key: SignatureStyleTypeEnum.DRAW,
                label: 'Draw',
                children: (
                  <SignatureCanvas
                    canvasProps={{
                      className: 'w-full h-[190px]'
                    }}
                    ref={ref}
                    extra={
                      <>
                        {!vmData.isBegin && (
                          <div className="absolute text-sm text-gray-600 text-center top-[40%] left-[43%]">
                            Sign Here
                          </div>
                        )}
                        <div className="text-xs text-gray-600 text-center w-full break-all">{user?.displayAddress}</div>
                      </>
                    }
                    onBeginStroke={vm.handleBeginStroke}
                    onEndStroke={vm.handleEndStroke}
                  />
                )
              },
              {
                key: SignatureStyleTypeEnum.UPLOAD,
                label: 'Upload',
                children: <ImageUploadWithCropper ref={copperRef} />
              },
              {
                key: SignatureStyleTypeEnum.STYLES,
                label: 'Styles',
                children: <SignStylesList value={vmData.signStyle} onChange={vm.setSignStyle} data={data} />
              }
            ]}
          />
        </div>
      </Modal>
    </>
  );
};
